import type { StoreNames } from '@highlight/ipc'

export const MAIN_PROCESS_ORIGIN_ID = 'oid:browser-main'

/**
 * IPC namespace for each EncryptedJsonStore, keyed by StoreNames.
 * Typed as Record<StoreNames, string> so TS errors if a store is added
 * to StoreNames but not mapped here.
 *
 * Note: 'apps-preferences' maps to constructor name 'app-preferences' (legacy).
 */
const STORE_IPC_NAMESPACES: Record<StoreNames, string> = {
  'apps-preferences': 'app-preferences.store',
  'app-state': 'app-state.store',
  settings: 'settings.store',
  user: 'user.store',
  general: 'general.store',
  superpowers: 'superpowers.store',
  facts: 'facts.store',
  windows: 'windows.store',
  conversations: 'conversations.store',
  telemetry: 'telemetry.store',
  workos: 'workos.store',
  device: 'device.store',
  chat: 'chat.store',
  flags: 'flags.store',
  billing: 'billing.store',
}

export const KNOWN_STORE_NAMESPACES = ['global.state', 'global.events', ...Object.values(STORE_IPC_NAMESPACES)] as const

const browserStoreSnapshot = new Map<string, Record<string, unknown>>()

export const cloneStoreState = <T extends Record<string, unknown>>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value)) as T
  } catch {
    return { ...value }
  }
}

export const ensureStoreSnapshotNamespace = (namespace: string): Record<string, unknown> => {
  const existingSnapshot = browserStoreSnapshot.get(namespace)
  if (existingSnapshot) {
    return existingSnapshot
  }

  const emptySnapshot: Record<string, unknown> = {}
  browserStoreSnapshot.set(namespace, emptySnapshot)
  return emptySnapshot
}

export const getStoreSnapshot = (namespace: string): Record<string, unknown> => {
  return cloneStoreState(ensureStoreSnapshotNamespace(namespace))
}

export const setStoreSnapshot = (namespace: string, state: Record<string, unknown>): void => {
  browserStoreSnapshot.set(namespace, cloneStoreState(state))
}

const setSnapshotPath = (storeState: Record<string, unknown>, keyPath: string, value: unknown): void => {
  const segments = keyPath.split('.').filter(Boolean)
  if (segments.length === 0) {
    return
  }

  if (segments.length === 1) {
    storeState[segments[0]] = value
    return
  }

  let cursor: Record<string, unknown> = storeState
  for (const segment of segments.slice(0, -1)) {
    if (typeof cursor[segment] !== 'object' || cursor[segment] === null) {
      cursor[segment] = {}
    }
    cursor = cursor[segment] as Record<string, unknown>
  }

  cursor[segments[segments.length - 1]] = value
}

const deleteSnapshotPath = (storeState: Record<string, unknown>, keyPath: string): void => {
  const segments = keyPath.split('.').filter(Boolean)
  if (segments.length === 0) {
    return
  }

  if (segments.length === 1) {
    delete storeState[segments[0]]
    return
  }

  let cursor: Record<string, unknown> | undefined = storeState
  for (const segment of segments.slice(0, -1)) {
    if (!cursor || typeof cursor !== 'object') {
      return
    }
    cursor = (cursor as Record<string, unknown>)[segment] as Record<string, unknown> | undefined
  }

  if (!cursor || typeof cursor !== 'object') {
    return
  }

  delete cursor[segments[segments.length - 1]]
}

const PERSISTED_NAMESPACES = new Set(['settings.store'])
const STORAGE_PREFIX = 'highlight:store:'

function persistToLocalStorage(namespace: string): void {
  if (!PERSISTED_NAMESPACES.has(namespace)) return
  try {
    const state = browserStoreSnapshot.get(namespace)
    if (state) {
      localStorage.setItem(`${STORAGE_PREFIX}${namespace}`, JSON.stringify(state))
    }
  } catch {
    // localStorage may be full or unavailable
  }
}

export function loadPersistedStore(namespace: string): Record<string, unknown> | null {
  if (!PERSISTED_NAMESPACES.has(namespace)) return null
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${namespace}`)
    if (raw) return JSON.parse(raw) as Record<string, unknown>
  } catch {
    // corrupt data, ignore
  }
  return null
}

/**
 * `InterProcessStore` batches a write as a single 'mutate' event. The payload
 * crosses an IPC boundary untyped, so an unrecognised op is dropped rather than
 * applied as an empty set.
 */
const readMutation = (payload: unknown): { op: string; args: Array<unknown> } | null => {
  if (!payload || typeof payload !== 'object') return null
  const { op, key, value } = payload as Record<string, unknown>
  if (op !== 'set' && op !== 'delete') return null
  return { op, args: [key, value] }
}

export const applyStoreSnapshotMutation = (namespace: string, eventName: string, eventArgs: Array<unknown>): void => {
  const storeState = ensureStoreSnapshotNamespace(namespace)

  // 'mutate' carries the op in its payload; everything else names it directly.
  const mutation = eventName === 'mutate' ? readMutation(eventArgs[0]) : { op: eventName, args: eventArgs }
  if (!mutation) return
  const { op, args } = mutation

  if (op === 'set') {
    const key = typeof args[0] === 'string' ? args[0] : undefined
    if (!key) {
      return
    }
    setSnapshotPath(storeState, key, args[1])
    persistToLocalStorage(namespace)
    return
  }

  if (op === 'delete') {
    const key = typeof args[0] === 'string' ? args[0] : undefined
    if (!key) {
      return
    }
    deleteSnapshotPath(storeState, key)
    persistToLocalStorage(namespace)
    return
  }

  if (op === 'set.all') {
    const allState = args[0]
    if (allState && typeof allState === 'object') {
      browserStoreSnapshot.set(namespace, cloneStoreState(allState as Record<string, unknown>))
      persistToLocalStorage(namespace)
    }
  }
}

export const emitBrowserMainStoreSet = (namespace: string, key: string, value: unknown): void => {
  try {
    if (!window.ipc?.channels?.events) {
      return
    }

    const transport = window.ipc.channels.events

    // InterProcessStore applies the mutation and notifies its local key subscribers.
    transport.send(namespace, MAIN_PROCESS_ORIGIN_ID, 'mutate', { op: 'set', key, value })
  } catch (error) {
    console.warn('[browser-runtime] Failed to emit store update', { namespace, key }, error)
  }
}
